import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { dashboardApi } from '@/lib/api'
import type {
  DashboardFilters,
  DashboardSummary,
  KilosByClient,
  KilosBySubcategory,
  KilosByDestination,
  MonthlyTrendPoint,
  OperationalAlerts,
} from '@/types'

export const dashboardKeys = {
  all: ['dashboard'] as const,
  summary: (f?: DashboardFilters) => [...dashboardKeys.all, 'summary', f] as const,
  byClient: (f?: DashboardFilters) => [...dashboardKeys.all, 'by-client', f] as const,
  bySubcategory: (f?: DashboardFilters) => [...dashboardKeys.all, 'by-subcategory', f] as const,
  byDestination: (f?: DashboardFilters) => [...dashboardKeys.all, 'by-destination', f] as const,
  monthlyTrend: (f?: DashboardFilters) => [...dashboardKeys.all, 'monthly-trend', f] as const,
  operationalAlerts: (f?: DashboardFilters) => [...dashboardKeys.all, 'operational-alerts', f] as const,
}

export function useDashboardSummary(filters?: DashboardFilters): UseQueryResult<DashboardSummary> {
  return useQuery({
    queryKey: dashboardKeys.summary(filters),
    queryFn: async () => (await dashboardApi.summary(filters)).data,
  })
}

export function useDashboardByClient(filters?: DashboardFilters): UseQueryResult<KilosByClient[]> {
  return useQuery({
    queryKey: dashboardKeys.byClient(filters),
    queryFn: async () => (await dashboardApi.byClient(filters)).data,
  })
}

export function useDashboardBySubcategory(filters?: DashboardFilters): UseQueryResult<KilosBySubcategory[]> {
  return useQuery({
    queryKey: dashboardKeys.bySubcategory(filters),
    queryFn: async () => (await dashboardApi.bySubcategory(filters)).data,
  })
}

export function useDashboardByDestination(filters?: DashboardFilters): UseQueryResult<KilosByDestination[]> {
  return useQuery({
    queryKey: dashboardKeys.byDestination(filters),
    queryFn: async () => (await dashboardApi.byDestination(filters)).data,
  })
}

export function useDashboardMonthlyTrend(filters?: DashboardFilters): UseQueryResult<MonthlyTrendPoint[]> {
  return useQuery({
    queryKey: dashboardKeys.monthlyTrend(filters),
    queryFn: async () => (await dashboardApi.monthlyTrend(filters)).data,
  })
}

export function useDashboardOperationalAlerts(filters?: DashboardFilters): UseQueryResult<OperationalAlerts> {
  return useQuery({
    queryKey: dashboardKeys.operationalAlerts(filters),
    queryFn: async () => (await dashboardApi.operationalAlerts(filters)).data,
  })
}
